import {
  ActionIcon,
  Alert,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  List,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconClipboard,
  IconDownload,
  IconEdit,
  IconExclamationCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconShieldCheck,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FormEvent } from "react";

import {
  useAdminAlarmRuleExport,
  useAdminAlarmRules,
  useCreateAdminAlarmRule,
  useDeleteAdminAlarmRule,
  useUpdateAdminAlarmRule,
} from "@/api/queries";
import {
  ALARM_RULE_STATUSES,
  ALARM_SEVERITIES,
  type AdminAlarmRule,
  type AlarmRuleStatus,
  type AlarmSeverity,
} from "@/api/types";
import { ErrorBanner } from "@/components/ErrorBanner";

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: AlarmRuleStatus; label: string }[] = [
  { value: "draft", label: "Draft" },
  { value: "ready", label: "Ready for export" },
  { value: "retired", label: "Retired" },
];

const SEVERITY_OPTIONS: { value: AlarmSeverity; label: string }[] = [
  { value: "info", label: "Info" },
  { value: "warning", label: "Warning" },
  { value: "critical", label: "Critical" },
];

type FiltersState = {
  status: string;
  severity: string;
  search: string;
};

type ModalState =
  | { kind: "none" }
  | { kind: "help" }
  | { kind: "export" }
  | { kind: "create" }
  | { kind: "edit"; rule: AdminAlarmRule }
  | { kind: "delete"; rule: AdminAlarmRule };

type NumericValue = number | "";

type FormValues = {
  name: string;
  code: string;
  severity: AlarmSeverity;
  status: AlarmRuleStatus;
  message: string;
  meter_slug: string;
  external_id: string;
  external_id_prefix: string;
  min_value: NumericValue;
  max_value: NumericValue;
  stale_after_seconds: NumericValue;
  gap_after_seconds: NumericValue;
  notes: string;
};

const EMPTY_FILTERS: FiltersState = { status: "", severity: "", search: "" };

export function AdminAlarmRulesPage() {
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<ModalState>({ kind: "none" });

  const queryParams = useMemo(
    () => ({
      status: asStatus(filters.status),
      severity: asSeverity(filters.severity),
      search: filters.search.trim() || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );
  const rulesQuery = useAdminAlarmRules(queryParams);
  const items = rulesQuery.data?.items ?? [];
  const total = rulesQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (key: keyof FiltersState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Admin</Text>
        <Text>Alarm rules</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Group gap="xs">
            <Title order={1}>Alarm rules</Title>
            <Tooltip label="Alarm rule setup help">
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Alarm rule setup help"
                onClick={() => setModal({ kind: "help" })}
              >
                <IconExclamationCircle size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Text c="dimmed" size="sm">
            Admin tuning workspace for telemetry alarm thresholds.
          </Text>
        </Stack>
        <Group>
          <Tooltip label="Refresh rules">
            <ActionIcon
              variant="default"
              size="lg"
              onClick={() => rulesQuery.refetch()}
              loading={rulesQuery.isFetching}
              aria-label="Refresh rules"
            >
              <IconRefresh size={18} />
            </ActionIcon>
          </Tooltip>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            onClick={() => setModal({ kind: "export" })}
          >
            Export ready rules
          </Button>
          <Button leftSection={<IconPlus size={16} />} onClick={() => setModal({ kind: "create" })}>
            Add rule
          </Button>
        </Group>
      </Group>

      <Alert
        icon={<IconShieldCheck size={18} />}
        color="green"
        variant="light"
        title="Safe tuning workspace"
      >
        Saved rules do not change live alarm behavior yet. The worker still uses the deployed
        environment configuration until a later cutover.
      </Alert>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <TextInput
            label="Search"
            placeholder="name, code, meter, or external ID"
            leftSection={<IconSearch size={14} />}
            value={filters.search}
            onChange={(event) => setFilter("search", event.currentTarget.value)}
            w={280}
          />
          <Select
            label="Status"
            placeholder="any"
            data={STATUS_OPTIONS}
            clearable
            value={filters.status || null}
            onChange={(value) => setFilter("status", value ?? "")}
            w={190}
          />
          <Select
            label="Severity"
            placeholder="any"
            data={SEVERITY_OPTIONS}
            clearable
            value={filters.severity || null}
            onChange={(value) => setFilter("severity", value ?? "")}
            w={160}
          />
        </Group>
      </Paper>

      {rulesQuery.error && <ErrorBanner error={rulesQuery.error} />}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Title order={3}>Rules</Title>
          <Text c="dimmed" size="sm">
            {total} rule{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {rulesQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No alarm rules match the current filters.</Text>
        ) : (
          <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm" miw={980}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Rule</Table.Th>
                  <Table.Th>Target</Table.Th>
                  <Table.Th>Condition</Table.Th>
                  <Table.Th>Severity</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((rule) => (
                  <Table.Tr key={rule.id}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text fw={500}>{rule.name}</Text>
                        <Text c="dimmed" size="xs" ff="monospace">
                          {rule.code}
                        </Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td>{targetLabel(rule)}</Table.Td>
                    <Table.Td>{conditionLabel(rule)}</Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={severityColor(rule.severity)}>
                        {severityLabel(rule.severity)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={statusColor(rule.status)}>
                        {statusLabel(rule.status)}
                      </Badge>
                    </Table.Td>
                    <Table.Td style={{ textAlign: "right" }}>
                      <Group gap="xs" justify="flex-end" wrap="nowrap">
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="subtle"
                            aria-label={`Edit ${rule.name}`}
                            onClick={() => setModal({ kind: "edit", rule })}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label={`Delete ${rule.name}`}
                            onClick={() => setModal({ kind: "delete", rule })}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>

      <AlarmRuleFormModal
        opened={modal.kind === "create"}
        onClose={() => setModal({ kind: "none" })}
        mode="create"
      />
      <AlarmRuleFormModal
        opened={modal.kind === "edit"}
        onClose={() => setModal({ kind: "none" })}
        mode="edit"
        rule={modal.kind === "edit" ? modal.rule : undefined}
      />
      <AlarmRuleExportModal
        opened={modal.kind === "export"}
        onClose={() => setModal({ kind: "none" })}
      />
      <AlarmRuleDeleteModal
        opened={modal.kind === "delete"}
        onClose={() => setModal({ kind: "none" })}
        rule={modal.kind === "delete" ? modal.rule : null}
      />
      <AlarmRuleHelpModal opened={modal.kind === "help"} onClose={() => setModal({ kind: "none" })} />
    </Stack>
  );
}

function AlarmRuleFormModal({
  opened,
  onClose,
  mode,
  rule,
}: {
  opened: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  rule?: AdminAlarmRule;
}) {
  const createMutation = useCreateAdminAlarmRule();
  const updateMutation = useUpdateAdminAlarmRule();
  const mutation = mode === "create" ? createMutation : updateMutation;
  const [values, setValues] = useState<FormValues>(() => toForm(rule));
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setValues(toForm(rule));
      setFormError(null);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, mode, rule?.id]);

  const set = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setFormError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const error = validate(values);
    setFormError(error);
    if (error) return;
    try {
      if (mode === "create") {
        await createMutation.mutateAsync(toBody(values));
      } else if (rule) {
        await updateMutation.mutateAsync({ id: rule.id, body: toBody(values) });
      }
      onClose();
    } catch {
      // Rendered below.
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={mode === "create" ? "Add alarm rule" : `Edit ${rule?.name ?? "alarm rule"}`}
      size="lg"
      centered
    >
      <form onSubmit={handleSubmit} noValidate>
        <Stack>
          {formError && (
            <Alert color="red" variant="light" title="Check the rule">
              {formError}
            </Alert>
          )}
          {mutation.error && <ErrorBanner error={mutation.error} title="Save failed" />}
          <Group grow align="flex-start">
            <TextInput
              label="Rule name"
              required
              value={values.name}
              onChange={(event) => set("name", event.currentTarget.value)}
            />
            <TextInput
              label="Rule code"
              required
              description="Lowercase ID used in alarm records."
              value={values.code}
              onChange={(event) => set("code", event.currentTarget.value)}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Severity"
              data={SEVERITY_OPTIONS}
              allowDeselect={false}
              value={values.severity}
              onChange={(value) => set("severity", asSeverity(value) ?? "warning")}
            />
            <Select
              label="Status"
              data={STATUS_OPTIONS}
              allowDeselect={false}
              value={values.status}
              onChange={(value) => set("status", asStatus(value) ?? "draft")}
            />
          </Group>
          <Textarea
            label="Operator message"
            minRows={2}
            value={values.message}
            onChange={(event) => set("message", event.currentTarget.value)}
          />
          <Group grow align="flex-start">
            <TextInput
              label="Meter ID"
              description="Optional meter slug to narrow the rule."
              value={values.meter_slug}
              onChange={(event) => set("meter_slug", event.currentTarget.value)}
            />
            <TextInput
              label="Exact external ID"
              value={values.external_id}
              onChange={(event) => set("external_id", event.currentTarget.value)}
            />
            <TextInput
              label="External ID prefix"
              value={values.external_id_prefix}
              onChange={(event) => set("external_id_prefix", event.currentTarget.value)}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="Minimum value"
              value={values.min_value}
              onChange={(value) => set("min_value", numeric(value))}
            />
            <NumberInput
              label="Maximum value"
              value={values.max_value}
              onChange={(value) => set("max_value", numeric(value))}
            />
            <NumberInput
              label="Stale after"
              description="Seconds without fresh telemetry."
              min={0}
              value={values.stale_after_seconds}
              onChange={(value) => set("stale_after_seconds", numeric(value))}
            />
            <NumberInput
              label="Gap after"
              description="Seconds between samples."
              min={0}
              value={values.gap_after_seconds}
              onChange={(value) => set("gap_after_seconds", numeric(value))}
            />
          </Group>
          <Textarea
            label="Admin notes"
            minRows={2}
            value={values.notes}
            onChange={(event) => set("notes", event.currentTarget.value)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              {mode === "create" ? "Create rule" : "Save changes"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function AlarmRuleExportModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const exportQuery = useAdminAlarmRuleExport(opened);
  const [copied, setCopied] = useState(false);
  const json = exportQuery.data?.rules_json ?? "";

  useEffect(() => {
    setCopied(false);
  }, [json, opened]);

  const handleCopy = async () => {
    if (!json) return;
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Export ready alarm rules" size="xl">
      <Stack>
        <Group justify="space-between">
          <Badge variant="light" color="gray">
            {exportQuery.data?.env_var ?? "EMS_WORKER_ALARM_RULES"}
          </Badge>
          <Text c="dimmed" size="sm">
            {exportQuery.data?.rule_count ?? 0} ready rule
            {exportQuery.data?.rule_count === 1 ? "" : "s"}
          </Text>
        </Group>
        {exportQuery.error && <ErrorBanner error={exportQuery.error} />}
        {exportQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : (
          <Textarea
            label="Worker rule JSON"
            description="Review-only export. The worker is not reading DB rules yet."
            value={json}
            readOnly
            autosize
            minRows={12}
            maxRows={18}
            styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
          />
        )}
        <Group justify="flex-end">
          <Button
            variant="default"
            leftSection={<IconRefresh size={16} />}
            onClick={() => exportQuery.refetch()}
            loading={exportQuery.isFetching}
          >
            Refresh
          </Button>
          <Button leftSection={<IconClipboard size={16} />} onClick={handleCopy} disabled={!json}>
            {copied ? "Copied" : "Copy JSON"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function AlarmRuleDeleteModal({
  opened,
  onClose,
  rule,
}: {
  opened: boolean;
  onClose: () => void;
  rule: AdminAlarmRule | null;
}) {
  const deleteMutation = useDeleteAdminAlarmRule();
  useEffect(() => {
    if (opened) deleteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, rule?.id]);

  const handleDelete = async () => {
    if (!rule) return;
    try {
      await deleteMutation.mutateAsync(rule.id);
      onClose();
    } catch {
      // Rendered below.
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Delete alarm rule" centered>
      <Stack>
        {deleteMutation.error && <ErrorBanner error={deleteMutation.error} title="Delete failed" />}
        <Text>
          Delete{" "}
          <Text span fw={600}>
            {rule?.name ?? "this rule"}
          </Text>
          ?
        </Text>
        <Text size="sm" c="dimmed">
          This removes the saved draft only. Existing alarm records remain unchanged.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleDelete()} loading={deleteMutation.isPending}>
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function AlarmRuleHelpModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  return (
    <Modal opened={opened} onClose={onClose} title="Alarm rule setup help" size="lg">
      <Stack>
        <Text size="sm">
          Use this page to prepare alarm thresholds in plain operational terms before promoting
          them into the worker configuration.
        </Text>
        <List spacing="sm" size="sm">
          <List.Item>Draft rules are saved for review and are not included in the export.</List.Item>
          <List.Item>Ready rules are included in the JSON export for deployment review.</List.Item>
          <List.Item>
            Choose at least one condition: minimum, maximum, stale telemetry, or sample gap.
          </List.Item>
          <List.Item>
            Use an exact external ID for one point, or an external ID prefix for a group.
          </List.Item>
        </List>
      </Stack>
    </Modal>
  );
}

function toForm(rule?: AdminAlarmRule): FormValues {
  return {
    name: rule?.name ?? "",
    code: rule?.code ?? "",
    severity: rule?.severity ?? "warning",
    status: rule?.status ?? "draft",
    message: rule?.message ?? "",
    meter_slug: rule?.meter_slug ?? "",
    external_id: rule?.external_id ?? "",
    external_id_prefix: rule?.external_id_prefix ?? "",
    min_value: rule?.min_value ?? "",
    max_value: rule?.max_value ?? "",
    stale_after_seconds: rule?.stale_after_seconds ?? "",
    gap_after_seconds: rule?.gap_after_seconds ?? "",
    notes: rule?.notes ?? "",
  };
}

function toBody(values: FormValues) {
  return {
    name: values.name.trim(),
    code: values.code.trim(),
    severity: values.severity,
    status: values.status,
    message: optionalString(values.message),
    meter_slug: optionalString(values.meter_slug),
    external_id: optionalString(values.external_id),
    external_id_prefix: optionalString(values.external_id_prefix),
    min_value: nullableNumber(values.min_value),
    max_value: nullableNumber(values.max_value),
    stale_after_seconds: nullableNumber(values.stale_after_seconds),
    gap_after_seconds: nullableNumber(values.gap_after_seconds),
    notes: optionalString(values.notes),
  };
}

function validate(values: FormValues): string | null {
  if (!values.name.trim() || !values.code.trim()) return "Rule name and code are required.";
  if (values.external_id.trim() && values.external_id_prefix.trim()) {
    return "Choose either exact external ID or external ID prefix, not both.";
  }
  const min = nullableNumber(values.min_value);
  const max = nullableNumber(values.max_value);
  const stale = nullableNumber(values.stale_after_seconds);
  const gap = nullableNumber(values.gap_after_seconds);
  if (min === null && max === null && stale === null && gap === null) {
    return "Add at least one threshold or timing condition.";
  }
  if (min !== null && max !== null && min > max) {
    return "Minimum value cannot be above maximum value.";
  }
  if ((stale !== null && stale <= 0) || (gap !== null && gap <= 0)) {
    return "Stale-after and gap-after values must be greater than zero.";
  }
  return null;
}

function numeric(value: string | number): NumericValue {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : "";
  }
  return "";
}

function nullableNumber(value: NumericValue): number | null {
  return value === "" ? null : value;
}

function optionalString(value: string): string | null {
  const stripped = value.trim();
  return stripped || null;
}

function asStatus(value: string | null): AlarmRuleStatus | undefined {
  return value && (ALARM_RULE_STATUSES as readonly string[]).includes(value)
    ? (value as AlarmRuleStatus)
    : undefined;
}

function asSeverity(value: string | null): AlarmSeverity | undefined {
  return value && (ALARM_SEVERITIES as readonly string[]).includes(value)
    ? (value as AlarmSeverity)
    : undefined;
}

function statusLabel(status: AlarmRuleStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}

function severityLabel(severity: AlarmSeverity): string {
  return SEVERITY_OPTIONS.find((option) => option.value === severity)?.label ?? severity;
}

function statusColor(status: AlarmRuleStatus): string {
  if (status === "ready") return "green";
  if (status === "retired") return "gray";
  return "blue";
}

function severityColor(severity: AlarmSeverity): string {
  if (severity === "critical") return "red";
  if (severity === "warning") return "yellow";
  return "blue";
}

function targetLabel(rule: AdminAlarmRule): string {
  if (rule.external_id) return rule.external_id;
  if (rule.external_id_prefix) return `${rule.external_id_prefix}*`;
  if (rule.meter_slug) return rule.meter_slug;
  return "All matching points";
}

function conditionLabel(rule: AdminAlarmRule): string {
  const parts: string[] = [];
  if (rule.min_value !== null) parts.push(`min ${rule.min_value}`);
  if (rule.max_value !== null) parts.push(`max ${rule.max_value}`);
  if (rule.stale_after_seconds !== null) parts.push(`stale ${rule.stale_after_seconds}s`);
  if (rule.gap_after_seconds !== null) parts.push(`gap ${rule.gap_after_seconds}s`);
  return parts.join(", ");
}
